import React, { useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FileUploadSection = ({ formData, handleFileUpload, handleFileRemove }) => {
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    const files = Array.from(e?.dataTransfer?.files);
    files?.forEach(file => handleFileUpload(file));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const getFileIcon = (fileName) => {
    const extension = fileName?.split('.')?.pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return 'FileText';
      case 'doc': case'docx': return 'FileText';
      case 'xls': case'xlsx': return 'FileSpreadsheet';
      case 'jpg': case'jpeg': case'png': case'gif': return 'Image';
      case 'zip': case'rar': return 'Archive';
      default: return 'File';
    }
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-border pb-4">
        <h3 className="text-xl font-semibold text-foreground mb-2">Project Documents</h3>
        <p className="text-text-secondary text-sm">
          Upload any relevant documents, wireframes, or reference materials (Optional)
        </p>
      </div>
      <div
        className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-accent/50 transition-colors duration-200"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <Icon name="Upload" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h4 className="text-lg font-medium text-foreground mb-2">
          Drop files here or click to upload
        </h4>
        <p className="text-text-secondary text-sm mb-4">
          Supported formats: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, ZIP (Max 10MB each)
        </p>
        
        <Button
          variant="outline"
          onClick={() => fileInputRef?.current?.click()}
          iconName="Plus"
          iconPosition="left"
        >
          Choose Files
        </Button>
        
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.zip,.rar"
          onChange={(e) => {
            Array.from(e?.target?.files)?.forEach(file => handleFileUpload(file));
            e.target.value = '';
          }}
          className="hidden"
        />
      </div>
      {formData?.uploadedFiles && formData?.uploadedFiles?.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium text-foreground">Uploaded Files ({formData?.uploadedFiles?.length})</h4>
          <div className="space-y-2">
            {formData?.uploadedFiles?.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-card border border-border rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <Icon 
                    name={getFileIcon(file?.name)} 
                    size={20} 
                    className="text-accent" 
                  />
                  <div>
                    <p className="text-sm font-medium text-card-foreground truncate max-w-xs">
                      {file?.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(file?.size)}
                    </p>
                  </div>
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleFileRemove(index)}
                  iconName="X"
                  className="text-muted-foreground hover:text-error"
                />
              </div>
            ))}
          </div>
          
          <div className="text-xs text-text-secondary">
            Total files: {formData?.uploadedFiles?.length} | 
            Total size: {formatFileSize(formData?.uploadedFiles?.reduce((acc, file) => acc + file?.size, 0))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadSection;